import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { hasSubscriberAccess } from "@/lib/subscription";
import "@/App.css";

import Landing from "@/pages/Landing";
import About from "@/pages/About";
import Careers from "@/pages/Careers";
import Diversity from "@/pages/Diversity";
import Investors from "@/pages/Investors";
import CarouselPlayer from "@/pages/CarouselPlayer";
import PropertyManagementCity from "@/pages/PropertyManagementCity";
import CentralRentalsEdmonton from "@/pages/CentralRentalsEdmonton";
import PropertyManagementSoftwareCanada from "@/pages/PropertyManagementSoftwareCanada";
import AlbertaSmallLandlords from "@/pages/AlbertaSmallLandlords";
import QuebecLandlordsFr from "@/pages/QuebecLandlordsFr";
import Community from "@/pages/Community";
import LandlordResources from "@/pages/LandlordResources";
import LandlordDiscounts from "@/pages/LandlordDiscounts";
import SmartAutomations from "@/pages/SmartAutomations";
const PricingRecommendation = lazy(() => import("@/pages/automations/PricingRecommendation"));
const InvoiceGenerator = lazy(() => import("@/pages/tools/InvoiceGenerator"));
const ColdEmailBuilder = lazy(() => import("@/pages/tools/ColdEmailBuilder"));
const CanadianLandlordStats = lazy(() => import("@/pages/reports/CanadianLandlordStats"));
const SmallLandlordSoftware = lazy(() => import("@/pages/SmallLandlordSoftware"));
const PropertyManagementCompanies = lazy(() => import("@/pages/PropertyManagementCompanies"));
const BestPropertyManagementCanada = lazy(() => import("@/pages/BestPropertyManagementCanada"));
const ExpenseForecast = lazy(() => import("@/pages/automations/ExpenseForecast"));
const MarketingHub = lazy(() => import("@/pages/MarketingHub"));
const LeasingAssistant = lazy(() => import("@/pages/LeasingAssistant"));
const RubsCalculator = lazy(() => import("@/pages/RubsCalculator"));
const EvictionWorkflow = lazy(() => import("@/pages/EvictionWorkflow"));
const KeyWall = lazy(() => import("@/pages/KeyWall"));
const GovSuite = lazy(() => import("@/pages/GovSuite"));
const ForGovernment = lazy(() => import("@/pages/ForGovernment"));
const CloudBrowsers = lazy(() => import("@/pages/CloudBrowsers"));
const CrmElectricalContractors = lazy(() => import("@/pages/CrmElectricalContractors"));
const ApplicationSummary = lazy(() => import("@/pages/ApplicationSummary"));
const ApiKeys = lazy(() => import("@/pages/ApiKeys"));
const StripeReturn = lazy(() => import("@/pages/StripeReturn"));
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import RoleSelect from "@/pages/RoleSelect";
import PublicProperty from "@/pages/PublicProperty";
import ApplyPage from "@/pages/ApplyPage";
import Dashboard from "@/pages/Dashboard";
import LandlordDashboardV2 from "@/pages/LandlordDashboardV2";
import TenantHomeV2 from "@/pages/TenantHomeV2";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Leases from "@/pages/Leases";
import LeaseDetail from "@/pages/LeaseDetail";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import Payments from "@/pages/Payments";
import Applications from "@/pages/Applications";
import Expenses from "@/pages/Expenses";
import Billing from "@/pages/Billing";
import Settings from "@/pages/Settings";
import PaymentInstructions from "@/pages/PaymentInstructions";
import DiscoveryAgent from "@/pages/DiscoveryAgent";
import Reports from "@/pages/Reports";
import Vendors from "@/pages/Vendors";
import WorkOrders from "@/pages/WorkOrders";
import Inspections from "@/pages/Inspections";
import Insurance from "@/pages/Insurance";
import Notices from "@/pages/Notices";
import AccessKeys from "@/pages/AccessKeys";
import SmartLocks from "@/pages/SmartLocks";
import Tours from "@/pages/Tours";
import SmartTours from "@/pages/SmartTours";
import MlsListings from "@/pages/MlsListings";
import LeaseSign from "@/pages/LeaseSign";
import LeaseEnvelopes from "@/pages/LeaseEnvelopes";
import MaintenanceRequests from "@/pages/MaintenanceRequests";
import TenantBillingExtras from "@/pages/TenantBillingExtras";
import PayBills from "@/pages/PayBills";
import MyVendors from "@/pages/MyVendors";
import CRAFiling from "@/pages/CRAFiling";
import LandlordTeam from "@/pages/LandlordTeam";
import AcceptInvite from "@/pages/AcceptInvite";
import Announcements from "@/pages/Announcements";
import TenantAnnouncements from "@/pages/TenantAnnouncements";
import CashApp from "@/pages/CashApp";
import IntegrationsHealth from "@/pages/IntegrationsHealth";
import IntegrationsHub from "@/pages/IntegrationsHub";
import BankAccountSettings from "@/pages/BankAccountSettings";
const AdminLetters = lazy(() => import("@/pages/AdminLetters"));
import RentAnalysis from "@/pages/RentAnalysis";
import Assistant from "@/pages/Assistant";
import Investor from "@/pages/Investor";
import Messages from "@/pages/Messages";
import Documents from "@/pages/Documents";
import Wellness from "@/pages/Wellness";
import LateRentAutopilot from "@/pages/LateRentAutopilot";
import TenantScore from "@/pages/TenantScore";
const LandlordMoneyHub = lazy(() => import("@/pages/LandlordMoneyHub"));
const TenantKeys = lazy(() => import("@/pages/TenantKeys"));
const KeylessUnlock = lazy(() => import("@/pages/KeylessUnlock"));
const ConciergeHub = lazy(() => import("@/pages/ConciergeHub"));
const ServiceWorkspace = lazy(() => import("@/pages/ServiceWorkspace"));
const ServiceRequestDetail = lazy(() => import("@/pages/ServiceRequestDetail"));
const TenantRepairs = lazy(() => import("@/pages/TenantRepairs"));
import RoiCalculator from "@/pages/tools/RoiCalculator";
import ExpandTool from "@/pages/tools/ExpandTool";
import AffordabilityCalculator from "@/pages/tools/AffordabilityCalculator";
const RentIncreaseNoticeTool = lazy(() => import("@/pages/RentIncreaseNoticeTool"));
const LateRentNoticeTool = lazy(() => import("@/pages/LateRentNoticeTool"));
const BatchRentRenewer = lazy(() => import("@/pages/BatchRentRenewer"));
const AdminProfitShare = lazy(() => import("@/pages/AdminProfitShare"));
const AdminBacklinkOutreach = lazy(() => import("@/pages/AdminBacklinkOutreach"));
const Insights = lazy(() => import("@/pages/Insights"));
const InsightArticle = lazy(() => import("@/pages/InsightArticle"));
const AlternativesMatrix = lazy(() => import("@/pages/AlternativesMatrix"));
const EditorialPolicy = lazy(() => import("@/pages/EditorialPolicy"));
const TenantRentPay = lazy(() => import("@/pages/TenantRentPay"));
const TenantResources = lazy(() => import("@/pages/TenantResources"));
const LandlordQuickSetup = lazy(() => import("@/pages/LandlordQuickSetup"));
const LandlordLeads = lazy(() => import("@/pages/LandlordLeads"));
import RentCalculator from "@/pages/RentCalculator";
import LeaveReview from "@/pages/LeaveReview";
import Compliance from "@/pages/Compliance";
import Utilities from "@/pages/Utilities";
import Referrals from "@/pages/Referrals";
import { Privacy, Terms, ScreeningDisclosure } from "@/pages/Legal";
import Blog, { BlogPost } from "@/pages/Blog";
import ReviewResponder from "@/pages/ReviewResponder";
import TapNConnect from "@/pages/TapNConnect";
import AffiliateRedirect from "@/pages/AffiliateRedirect";
import TrialStart from "@/pages/TrialStart";
import TrialWelcome from "@/pages/TrialWelcome";
import Province from "@/pages/Province";
import LandlordSoftwareHub from "@/pages/LandlordSoftwareHub";
import Unsubscribe from "@/pages/Unsubscribe";
import City from "@/pages/City";
import GetStarted from "@/pages/GetStarted";
import QuebecFr from "@/pages/QuebecFr";
import SmartFeatures from "@/pages/SmartFeatures";
import LandlordReport from "@/pages/LandlordReport";
import LandlordLaws from "@/pages/LandlordLaws";
import ForRolePage from "@/pages/ForRolePage";
import SearchPage from "@/pages/SearchPage";
import PropertyManagementProvince from "@/pages/PropertyManagementProvince";
import LandlordUniversity from "@/pages/LandlordUniversity";
import ViralPost from "@/pages/ViralPost";
import Follower from "@/pages/Follower";
import ListingStudio from "@/pages/ListingStudio";
const AdminMarketing = lazy(() => import("@/pages/AdminMarketing"));
const AdminClaim = lazy(() => import("@/pages/AdminClaim"));
const AdminErrorLog = lazy(() => import("@/pages/AdminErrorLog"));
const AdminSelfHeal = lazy(() => import("@/pages/AdminSelfHeal"));
const AdminFunnel = lazy(() => import("@/pages/AdminFunnel"));
const Directory = lazy(() => import("@/pages/Directory"));
const AdminCrawlerInsights = lazy(() => import("@/pages/AdminCrawlerInsights"));
const TenantRenewalPortal = lazy(() => import("@/pages/TenantRenewalPortal"));
const TenantToolkit = lazy(() => import("@/pages/TenantToolkit"));
const LeasingAssist = lazy(() => import("@/pages/LeasingAssist"));
const Partnership = lazy(() => import("@/pages/Partnership"));
const LandlordParking = lazy(() => import("@/pages/LandlordParking"));
const LandlordResolution = lazy(() => import("@/pages/LandlordResolution"));
const EmailDrafter = lazy(() => import("@/pages/EmailDrafter"));
const TenantHelpDesk = lazy(() => import("@/pages/TenantHelpDesk"));
const CopilotHub = lazy(() => import("@/pages/CopilotHub"));
const SubleaseMarketplace = lazy(() => import("@/pages/SubleaseMarketplace"));
const MigrationWizard = lazy(() => import("@/pages/MigrationWizard"));
const BuildiumHowTo = lazy(() => import("@/pages/BuildiumHowTo"));
const PMWorkspace = lazy(() => import("@/pages/PMWorkspace"));
const AutoAdvanceSettings = lazy(() => import("@/pages/AutoAdvanceSettings"));
const UseCaseLanding = lazy(() => import("@/pages/UseCaseLanding"));
const AdminCopilotTasks = lazy(() => import("@/pages/AdminCopilotTasks"));
const AdminLeads = lazy(() => import("@/pages/AdminLeads"));
const AdminSmsSettings = lazy(() => import("@/pages/AdminSmsSettings"));
const AdminWebmasterVerify = lazy(() => import("@/pages/AdminWebmasterVerify"));
const AdminBacklinkKit = lazy(() => import("@/pages/AdminBacklinkKit"));
const AdminRankTracker = lazy(() => import("@/pages/AdminRankTracker"));
import EqualHousing from "@/pages/EqualHousing";
import QuickQuestionWidget from "@/components/QuickQuestionWidget";
import AutoFixStatusChip from "@/components/AutoFixStatusChip";
import IdleLogout from "@/components/IdleLogout";
import RouteHints from "@/components/RouteHints";
import GoogleReviewPrompt from "@/components/GoogleReviewPrompt";
import TrustedSiteBadge from "@/components/TrustedSiteBadge";
import SeoOverrideInjector from "@/components/seo/SeoOverrideInjector";
import AutoSchema from "@/components/seo/AutoSchema";
import SiteBaseSchema from "@/components/seo/SiteBaseSchema";
import SmartSelectionChip from "@/components/smart/SmartSelectionChip";
import SmartOmnibar from "@/components/smart/SmartOmnibar";
import IntentEngine from "@/components/IntentEngine";
import AdFraudTracker from "@/components/AdFraudTracker";
import ErrorBoundary from "@/components/ErrorBoundary";
import FrLocale from "@/components/FrLocale";
const AdminStudio = lazy(() => import("@/pages/AdminStudio"));
const AdminBuildings = lazy(() => import("@/pages/AdminBuildings"));
const AdminCredit = lazy(() => import("@/pages/AdminCredit"));
const AdminSupport = lazy(() => import("@/pages/AdminSupport"));
import MortgageRates from "@/pages/MortgageRates";
const LandlordStorefront = lazy(() => import("@/pages/LandlordStorefront"));
const StorefrontEditor = lazy(() => import("@/pages/StorefrontEditor"));
const LandlordPMPortal = lazy(() => import("@/pages/LandlordPMPortal"));
const AdminPropertyData = lazy(() => import("@/pages/AdminPropertyData"));
const AdminAnalytics = lazy(() => import("@/pages/AdminAnalytics"));
import AdLanding from "@/pages/AdLanding";
import CompareVs, { CompareHub } from "@/pages/CompareVs";
const FeatureLanding = lazy(() => import("@/pages/FeatureLanding"));
const FeaturesHub = lazy(() => import("@/pages/FeaturesHub"));
const IntegrateLanding = lazy(() => import("@/pages/IntegrateLanding"));
const IntegrateHub = lazy(() => import("@/pages/IntegrateLanding").then((m) => ({ default: m.IntegrateHub })));
const BestSoftware = lazy(() => import("@/pages/BestSoftware"));
const BestHub = lazy(() => import("@/pages/BestSoftware").then((m) => ({ default: m.BestHub })));
const AdminSeoCopilot = lazy(() => import("@/pages/AdminSeoCopilot"));
const AdSearchLanding = lazy(() => import("@/pages/AdSearchLanding"));
const RevenueOptimizer = lazy(() => import("@/pages/RevenueOptimizer"));
const AdminContentEngine = lazy(() => import("@/pages/AdminContentEngine"));
import Answer, { AnswerHub } from "@/pages/Answer";
import Pricing from "@/pages/Pricing";
import FourSavers from "@/pages/FourSavers";
import FreeActions from "@/pages/FreeActions";
import Inspection from "@/pages/Inspection";
import Library from "@/pages/Library";
import Reviews from "@/pages/Reviews";
import TenantScreening from "@/pages/TenantScreening";
import ScreeningConsent from "@/pages/ScreeningConsent";
import Buildings from "@/pages/Buildings";
import BuildingHub from "@/pages/BuildingHub";
import MarketingAssets from "@/pages/MarketingAssets";
import TenantPlacement from "@/pages/TenantPlacement";
import FindAPlace from "@/pages/FindAPlace";
import RentalListing from "@/pages/RentalListing";
import Accessibility from "@/pages/Accessibility";
import AffiliateProgram from "@/pages/AffiliateProgram";
import AdminAffiliateApplications from "@/pages/AdminAffiliateApplications";
import ZeroEffortMigration from "@/pages/ZeroEffortMigration";
import AppFolioAlternatives from "@/pages/AppFolioAlternatives";
import AuthCallback from "@/pages/AuthCallback";
import VendorPortal from "@/pages/VendorPortal";
import GroupDrafts from "@/pages/GroupDrafts";
const AdminAdStudio = lazy(() => import("@/pages/AdminAdStudio"));
const AdminContentPack = lazy(() => import("@/pages/AdminContentPack"));
import ToolsRoute from "@/pages/Tools";
const AdminGrowthOS = lazy(() => import("@/pages/AdminGrowthOS"));
const AdminSeoAgent = lazy(() => import("@/pages/AdminSeoAgent"));
const AdminGeoAudit = lazy(() => import("@/pages/AdminGeoAudit"));
const LandlordFunds = lazy(() => import("@/pages/LandlordFunds"));
const AffordableHousingHub = lazy(() => import("@/pages/AffordableHousingHub"));
const AdminAutoImprove = lazy(() => import("@/pages/AdminAutoImprove"));
const PressKit = lazy(() => import("@/pages/PressKit"));
const PressHub = lazy(() => import("@/pages/PressHub"));
const PressForbesBrief = lazy(() => import("@/pages/PressForbesBrief"));
const PressWealthsimpleGuide = lazy(() => import("@/pages/PressWealthsimpleGuide"));
const PressInvestopediaExplainer = lazy(() => import("@/pages/PressInvestopediaExplainer"));
const PressOutreachKit = lazy(() => import("@/pages/PressOutreachKit"));
const PressHubFr = lazy(() => import("@/pages/PressHubFr"));
const PressForbesBriefFr = lazy(() => import("@/pages/PressForbesBriefFr"));
const PressWealthsimpleGuideFr = lazy(() => import("@/pages/PressWealthsimpleGuideFr"));
const PressInvestopediaExplainerFr = lazy(() => import("@/pages/PressInvestopediaExplainerFr"));
const PressOutreachKitFr = lazy(() => import("@/pages/PressOutreachKitFr"));
const LocalListings = lazy(() => import("@/pages/LocalListings"));
const InvestorRelations = lazy(() => import("@/pages/InvestorRelations"));
const IpoRoadmap = lazy(() => import("@/pages/IpoRoadmap"));
const Invest = lazy(() => import("@/pages/Invest"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const FrenchUseCase = lazy(() => import("@/pages/FrenchUseCase"));
const AiTools = lazy(() => import("@/pages/AiTools"));
const ProspectDiscovery = lazy(() => import("@/pages/ProspectDiscovery"));
const AiSearchOptimizer = lazy(() => import("@/pages/AiSearchOptimizer"));
const Auto = lazy(() => import("@/pages/Auto"));
const SharedResult = lazy(() => import("@/pages/SharedResult"));
const FindTenants = lazy(() => import("@/pages/FindTenants"));
import { PartnerApply, PartnerDashboard } from "@/pages/PartnerApply";
import BrokerageReferral from "@/pages/BrokerageReferral";
import EnterpriseIntegrations from "@/pages/EnterpriseIntegrations";
import ShareAndEarn from "@/pages/ShareAndEarn";
import SustainabilityHub, { SustainabilityProvince } from "@/pages/Sustainability";
import Changelog from "@/pages/Changelog";
import CreditBuilderHub, { CreditBuilderProvince } from "@/pages/CreditBuilder";
import Books from "@/pages/Books";
import Bills from "@/pages/Bills";
import BankRecon from "@/pages/BankRecon";
import CondoWorksIntegration from "@/pages/CondoWorksIntegration";
import ButterflyMxIntegration from "@/pages/ButterflyMxIntegration";
import Briefing from "@/pages/Briefing";
import Activity from "@/pages/Activity";
import AgentSettings from "@/pages/AgentSettings";
import SmartOperator from "@/pages/SmartOperator";
import Retention from "@/pages/Retention";
import InternalWorkspace from "@/pages/InternalWorkspace";
import MyBusiness from "@/pages/MyBusiness";
import VendorDetail from "@/pages/VendorDetail";
import VendorApply from "@/pages/VendorApply";
import RevenueEngine from "@/pages/RevenueEngine";
import TenantLetters from "@/pages/TenantLetters";
import SmartOperatorBubble from "@/components/SmartOperatorBubble";
import CopilotSearchWidget from "@/components/CopilotSearchWidget";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

import AppShell from "@/components/layout/AppShell";
import PaywallGuard from "@/components/PaywallGuard";
import { Loader } from "@/components/ui/Primitives";
import { installGoogleAdsTag, firePageView } from "@/lib/google-tag";
import { startWebVitalsReporter } from "@/lib/web-vitals";
import { captureFromUrl as captureAdAttribution } from "@/lib/attribution";
import { newEventId } from "@/lib/attribution";
import { track as trackAnalytics } from "@/lib/analytics";

// Install Google Ads gtag (no-op if env vars unset). Runs once at module
// import so the tag is in the DOM before the first ad-bot visit.
installGoogleAdsTag();
// Stream LCP/INP/CLS/TTFB/FCP into GTM dataLayer so we can track Core
// Web Vitals field data in GA4 alongside Search Console's aggregates.
startWebVitalsReporter();
// Snapshot ad-click + UTM params on first paint so they persist across
// the visit and can be forwarded to every conversion endpoint.
captureAdAttribution();

function lastPortal() {
  try { return localStorage.getItem("crc_last_portal") === "tenant" ? "tenant" : "landlord"; } catch { return "landlord"; }
}

// FlinksGate — hides the banking pages until we go live in production.
// Toggle via REACT_APP_FLINKS_VISIBLE=true in frontend/.env and restart.
function FlinksGate({ children }) {
  const visible = String(process.env.REACT_APP_FLINKS_VISIBLE || "").toLowerCase() === "true";
  if (visible) return children;
  return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="flinks-gate-hidden">
      <h1 className="text-3xl font-bold text-slate-900 mb-4">
        Bank connect · Coming soon
      </h1>
      <p className="text-base text-slate-600 leading-relaxed mb-8">
        Bank-account integration is undergoing final setup with our banking
        partner. Once we&apos;re live, this page will show your unified rent
        deposits, expense tracking, and cash-flow forecasting.
      </p>
      <p className="text-sm text-slate-500">
        We&apos;ll email every landlord the moment it opens. In the meantime,
        rent collection via Stripe and Klarna is fully working — head to
        Dashboard → Payments to set that up.
      </p>
    </div>
  );
}

function Protected({ children, requireRole = false, requirePaid = false, feature = "this feature", benefits = null }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loader label="Loading your account…" />;
  if (!user) return <Navigate to={`/login/${lastPortal()}`} state={{ from: loc.pathname }} replace />;
  if (requireRole && !user.role) return <Navigate to="/role-select" replace />;
  // Paid-landlord gate. Tenants and investors bypass this — the gate only
  // fires for role=landlord accounts without an active plan. Uses the
  // existing PaywallGuard component so the visual + copy is consistent
  // with the rest of the app.
  if (requirePaid && (user.role || "").toLowerCase() === "landlord") {
    const paid = hasSubscriberAccess(user);
    if (!paid) {
      return (
        <AppShell>
          <PaywallGuard feature={feature} benefits={benefits || undefined}>
            {children}
          </PaywallGuard>
        </AppShell>
      );
    }
  }
  return <AppShell>{children}</AppShell>;
}

// Landlord-only gate for pricing pages. We hide pricing from the public
// marketing surface — a prospect must sign in as a landlord to see the plan
// grid. Non-landlord roles bounce back to their home surface.
function LandlordOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login/landlord" replace />;
  const role = (user.role || "").toLowerCase();
  if (role && role !== "landlord") return <Navigate to="/dashboard" replace />;
  if (!user.role) return <Navigate to="/role-select" replace />;
  return <AppShell>{children}</AppShell>;
}

function PostAuthGate({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loader />;
  if (user) {
    if (!user.role) return <Navigate to="/role-select" replace />;
    // Respect ?next=… so flows like /admin/claim redirect back to
    // the intended destination instead of the default dashboard.
    const nextTarget = new URLSearchParams(loc.search).get("next");
    return <Navigate to={nextTarget && nextTarget.startsWith("/") ? nextTarget : "/dashboard"} replace />;
  }
  return children;
}

// Capture ?ref=CODE on ANY route (referral links point to the homepage) and
// stash it so Signup can auto-redeem it after account creation.
// Also fires a server-side track-click to attribute the visit to a Partner
// Program code if one matches (idempotent — duplicate clicks just get logged).
const analyticsPageLoadId = newEventId();
function ReferralCapture() {
  const loc = useLocation();
  const { user: analyticsUser, loading: analyticsAuthLoading } = useAuth();
  // Fire page_view on every client-side route change so the Google Ads
  // "Page view" conversion + GA4 page tracking stay Active (React Router
  // doesn't trigger Google's automatic pageview by itself).
  useEffect(() => {
    firePageView(loc.pathname + loc.search);
    captureAdAttribution();
  }, [loc.pathname, loc.search, loc.key]);
  useEffect(() => {
    if (!analyticsAuthLoading) trackAnalytics("page_view", { event_id: `pv_${analyticsPageLoadId}_${loc.key}`, page_path: loc.pathname, actor_role: analyticsUser?.role || "" });
  }, [loc.pathname, loc.search, loc.key, analyticsAuthLoading, analyticsUser?.role]);
  const ref = new URLSearchParams(loc.search).get("ref");
  if (ref) {
    try { localStorage.setItem("crc_ref", ref); } catch (_) { /* noop */ }
    // Fire-and-forget partner click attribution (matches: true|false silently)
    const params = new URLSearchParams(loc.search);
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const tracked = sessionStorage.getItem(`crc_partner_tracked_${ref}`);
    if (!tracked && apiBase) {
      sessionStorage.setItem(`crc_partner_tracked_${ref}`, "1");
      fetch(`${apiBase}/api/partners/track-click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: ref,
          utm_source: params.get("utm_source") || null,
          utm_medium: params.get("utm_medium") || null,
          session_id: localStorage.getItem("crc_session_id") || null,
        }),
      }).catch(() => {});
    }
  }
  return null;
}

function ServiceWorkQuietMode({ children }) {
  const { pathname } = useLocation();
  const focused = ["/admin", "/settings", "/get-started", "/blog", "/tools", "/landlord/concierge", "/service-workspace", "/service-requests", "/tenant/repairs", "/tenant-placement"].some((path) => pathname === path || pathname.startsWith(`${path}/`));
  return focused ? null : children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <ReferralCapture />
        <ServiceWorkQuietMode><QuickQuestionWidget /></ServiceWorkQuietMode>
        <AutoFixStatusChip />
        <IdleLogout />
        <RouteHints />
        <ServiceWorkQuietMode><GoogleReviewPrompt /></ServiceWorkQuietMode>
        <TrustedSiteBadge />
        <SeoOverrideInjector />
        {/* Route-aware JSON-LD injector — writes SoftwareApplication +
            Offer schema on routes that don't ship their own richer
            per-page schema. Idempotent; won't clash with the pages
            that inject their own (Landing, Pricing, Blog, Features,
            CompareVs, Integrate, Best). */}
        <AutoSchema />
        {/* Sitewide Organization + WebSite + BreadcrumbList schema —
            supplements AutoSchema. Organization/WebSite emit once at
            mount; BreadcrumbList refreshes on every route change. */}
        <SiteBaseSchema />
        {/* Global "Smart" text-selection chip — floats "Summarize /
            Rewrite / Ask Copilot" when the visitor highlights ≥40
            chars. Hidden on /admin, /login, /signup, /dashboard so it
            never fights the app's own selection UI. */}
        <ServiceWorkQuietMode><SmartSelectionChip /></ServiceWorkQuietMode>
        {/* Global Copilot FAB — one-click shortcuts to the most common
            generation flows (listing, lease, notice, rent reminder,
            revenue scan…). Hidden on public + admin + auth routes. */}
        <ServiceWorkQuietMode><SmartOmnibar /></ServiceWorkQuietMode>
        {/* Intent Engine — client-side, rule-based (zero LLM cost).
            Renders the Smart Social Proof toaster, hesitant-scroll nudge
            (pricing pages only), and the exit-intent 15% offer. Silently
            no-ops on authed portals and on visitors with prefers-reduced-motion. */}
        <IntentEngine />
        <AdFraudTracker />
        <ErrorBoundary>
        <Suspense fallback={
          <div className="min-h-[60vh] flex flex-col items-center justify-center bg-slate-50" data-testid="route-loading">
            <div className="h-9 w-9 border-[3px] border-emerald-600 border-t-transparent rounded-full animate-spin" />
            <div className="mt-3 text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Loading…</div>
          </div>
        }>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Navigate to="/login/landlord" replace />} />
          <Route path="/login/:portal" element={<PostAuthGate><Login /></PostAuthGate>} />
          <Route path="/signup" element={<PostAuthGate><Signup /></PostAuthGate>} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/vendor" element={<VendorPortal mode="auto" />} />
          <Route path="/vendor/login" element={<VendorPortal mode="login" />} />
          <Route path="/vendor/dashboard" element={<VendorPortal mode="dashboard" />} />
          <Route path="/p/:propertyId" element={<PublicProperty />} />
          <Route path="/apply/:propertyId" element={<ApplyPage />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/screening" element={<ScreeningDisclosure />} />
          <Route path="/legal/accessibility" element={<Accessibility />} />
          <Route path="/accessibility" element={<Accessibility />} />

          {/* Partners + Migration + Alternatives (SEO / marketing) */}
          <Route path="/partners" element={<AffiliateProgram />} />
          <Route path="/affiliate-program" element={<AffiliateProgram />} />
          <Route path="/migration" element={<ZeroEffortMigration />} />
          <Route path="/zero-effort-migration" element={<ZeroEffortMigration />} />
          <Route path="/alternatives/appfolio" element={<AppFolioAlternatives />} />
          <Route path="/admin/affiliate-applications" element={<AdminAffiliateApplications />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/tools/review-responder" element={<ReviewResponder />} />
          <Route path="/tools/roi-calculator" element={<RoiCalculator />} />
          <Route path="/tools/rent-increase-notice" element={<RentIncreaseNoticeTool />} />
          <Route path="/tools/n4-late-rent-notice" element={<LateRentNoticeTool />} />
          <Route path="/renewals/batch" element={<Protected requireRole requirePaid feature="Bulk rent-increase renewer" benefits={["One click for every expiring lease","Correct provincial form auto-picked","PDFs stored per tenant"]}><BatchRentRenewer /></Protected>} />
          <Route path="/admin/profit-share" element={<Protected requireRole><AdminProfitShare /></Protected>} />
          <Route path="/admin/backlink-outreach" element={<Protected requireRole><AdminBacklinkOutreach /></Protected>} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightArticle />} />
          <Route path="/alternatives" element={<AlternativesMatrix />} />
          <Route path="/editorial-policy" element={<EditorialPolicy />} />
          <Route path="/tenant/rent" element={<Protected requireRole><TenantRentPay /></Protected>} />
          <Route path="/l/:slug" element={<LandlordStorefront />} />
          <Route path="/landlord/storefront" element={<Protected requireRole="landlord"><StorefrontEditor /></Protected>} />
          <Route path="/landlord/pm-portal" element={<Protected requireRole="landlord"><LandlordPMPortal /></Protected>} />
          <Route path="/tenant/resources" element={<Protected requireRole><TenantResources /></Protected>} />
          <Route path="/landlord/setup" element={<Protected requireRole><LandlordQuickSetup /></Protected>} />
          <Route path="/landlord/leads" element={<Protected requireRole><LandlordLeads /></Protected>} />
          <Route path="/tools/expand" element={<ExpandTool />} />
          <Route path="/tools/rent-affordability" element={<AffordabilityCalculator />} />
          <Route path="/tools/invoice-generator" element={<InvoiceGenerator />} />
          <Route path="/tools/cold-email-builder" element={<ColdEmailBuilder />} />
          <Route path="/reports/canadian-landlord-statistics-2026" element={<CanadianLandlordStats />} />
          <Route path="/reports/canadian-landlord-statistics" element={<CanadianLandlordStats />} />
          <Route path="/small-landlord-software" element={<SmallLandlordSoftware />} />
          <Route path="/property-management-software-small-landlords" element={<SmallLandlordSoftware />} />
          <Route path="/property-management-companies" element={<PropertyManagementCompanies />} />
          <Route path="/property-management-software-companies" element={<PropertyManagementCompanies />} />
          <Route path="/pmc-software" element={<PropertyManagementCompanies />} />
          <Route path="/best-property-management-software-canada" element={<BestPropertyManagementCanada />} />
          <Route path="/top-property-management-software-canada" element={<BestPropertyManagementCanada />} />
          <Route path="/property-management-software-canada" element={<BestPropertyManagementCanada />} />
          <Route path="/r/:slug" element={<TapNConnect />} />
          <Route path="/a/:slug" element={<AffiliateRedirect />} />
          <Route path="/trial/start" element={<TrialStart />} />
          <Route path="/trial/welcome" element={<TrialWelcome />} />
          <Route path="/landlord-software" element={<LandlordSoftwareHub />} />
          <Route path="/landlord-software/:province" element={<Province />} />
          {/* Alias — captures searchers who type "Ontario Residential
              Tenancy Act" or "Alberta tenancy act" straight into the
              address bar. Reuses the Province page + JSON-LD. */}
          <Route path="/tenancy-act/:province" element={<Province />} />
          <Route path="/residential-tenancy-act/:province" element={<Province />} />
          <Route path="/credit-builder" element={<CreditBuilderHub />} />
          <Route path="/credit-builder/:province" element={<CreditBuilderProvince />} />
          <Route path="/mortgage-rates" element={<MortgageRates />} />
          <Route path="/sustainability" element={<SustainabilityHub />} />
          <Route path="/sustainability/:province" element={<SustainabilityProvince />} />
          <Route path="/landlord-software/city/:city" element={<City />} />
          <Route path="/fr/landlord-software/quebec" element={<QuebecFr />} />
          {/* Dedicated French-Canadian mirrors of the highest-SEO pages.
              Each is a distinct URL Google can index for fr-CA queries,
              wraps the EN page in <FrLocale> which flips <html lang>,
              injects hreflang + canonical, and triggers Google Translate
              to French so Québécois visitors see native copy. */}
          <Route path="/fr" element={<FrLocale enPath="/"><Landing /></FrLocale>} />
          <Route path="/fr/pricing" element={<FrLocale enPath="/pricing"><Pricing /></FrLocale>} />
          <Route path="/fr/laws/quebec" element={<FrLocale enPath="/laws/qc"><LandlordLaws /></FrLocale>} />
          <Route path="/fr/laws/qc" element={<FrLocale enPath="/laws/qc"><LandlordLaws /></FrLocale>} />
          <Route path="/fr/blog" element={<FrLocale enPath="/blog"><Blog /></FrLocale>} />
          <Route path="/fr/blog/:slug" element={<FrLocale><BlogPost /></FrLocale>} />
          <Route path="/fr/tools/roi-calculator" element={<FrLocale enPath="/tools/roi-calculator"><RoiCalculator /></FrLocale>} />
          <Route path="/fr/tools/rent-affordability" element={<FrLocale enPath="/tools/rent-affordability"><AffordabilityCalculator /></FrLocale>} />
          <Route path="/fr/property-management-software" element={<FrLocale enPath="/property-management-software"><PropertyManagementSoftwareCanada /></FrLocale>} />
          <Route path="/fr/about" element={<FrLocale enPath="/about"><About /></FrLocale>} />
          <Route path="/fr/signup" element={<FrLocale enPath="/signup"><PostAuthGate><Signup /></PostAuthGate></FrLocale>} />
          <Route path="/fr/login/:portal" element={<FrLocale><PostAuthGate><Login /></PostAuthGate></FrLocale>} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/lp" element={<GetStarted />} />
          <Route path="/lp/landlord" element={<AdLanding audience="landlord" />} />
          <Route path="/lp/tenant" element={<AdLanding audience="tenant" />} />
          <Route path="/vs" element={<CompareHub />} />
          <Route path="/vs/:competitor" element={<CompareVs />} />
          {/* Programmatic pain-point pages — long-tail commercial queries
              like "automated security deposit escrow software" and
              "free maintenance coordination portal". Same component
              hydrated with a per-slug data record. */}
          <Route path="/features" element={<FeaturesHub />} />
          <Route path="/features/:slug" element={<FeatureLanding />} />
          {/* Integration Query landing pages — target long-tail buyer
              queries like "Central Rentals Stripe integration" and
              "Central Rentals QuickBooks integration". Public and
              indexable — the paid /integrations dashboard sits behind
              a Protected route below. */}
          <Route path="/integrate" element={<IntegrateHub />} />
          <Route path="/integrate/:tool" element={<IntegrateLanding />} />
          {/* "Best property management software in [province]" — highest
              commercial-intent long-tail queries in the Canadian market.
              One page per province, all with SoftwareApplication +
              FAQPage schema for AI Overviews eligibility. */}
          <Route path="/best/property-management-software" element={<BestHub />} />
          <Route path="/best/property-management-software/:province" element={<BestSoftware />} />
          {/* Admin-only SEO Copilot — one screen for keyword clusters,
              cannibalization, freshness, internal-link injection,
              alt-text auto-fill, AEO probe (Claude/GPT/Gemini) and
              the distribution kit for Reddit/Medium/DEV.to/LinkedIn. */}
          <Route path="/admin/seo-copilot" element={<AdminSeoCopilot />} />
          {/* "Best [Competitor] Alternatives" aliases — hijack the exact
              query patterns Google surfaces for "X alternative Canada". */}
          <Route path="/alternatives/:competitor" element={<CompareVs />} />
          {/* Dedicated Google-Ads / Bing-Ads destination URLs — high
              Quality Score landing pages per keyword × province. Every
              province gets its own indexable path so ads route to a
              hyper-relevant page. See /app/memory/GOOGLE_ADS_GUIDE.md */}
          <Route path="/ads/:variant" element={<AdSearchLanding />} />
          <Route path="/ads/:variant/:province" element={<AdSearchLanding />} />
          {/* Revenue Optimizer — portfolio-wide Smart-rent recommendations,
              vacancy forecast and live market signals. Landlord-only, paid. */}
          <Route path="/revenue-optimizer" element={<Protected requireRole requirePaid feature="Revenue Optimizer" benefits={["Portfolio-wide Smart-rent recommendations refreshed daily", "Vacancy + lease-end forecast per unit", "CMHC + live-market signals visible on every row"]}><RevenueOptimizer /></Protected>} />
          <Route path="/answers" element={<AnswerHub />} />
          <Route path="/answers/:province/:topic" element={<Answer />} />
          <Route path="/fr/answers/:province/:topic" element={<Answer lang="fr" />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/four-savers" element={<FourSavers />} />
          <Route path="/free-actions" element={<FreeActions />} />
          <Route path="/inspection" element={<Protected requireRole requirePaid feature="Smart Inspection" benefits={["Structured move-in / move-out reports in 60 seconds","Room-by-room condition ratings + damage flags","Province-specific deposit-return recommendation","Downloadable Markdown report attachable to any lease"]}><Inspection /></Protected>} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/about" element={<About />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/diversity" element={<Diversity />} />
          <Route path="/investors" element={<Investors />} />
          <Route path="/deck" element={<CarouselPlayer mode="investor" />} />
          <Route path="/welcome-tour" element={<CarouselPlayer mode="onboarding" />} />
          <Route path="/property-management-software" element={<PropertyManagementSoftwareCanada />} />
          {/* /best-property-management-software-canada and /-canada and
              /top-property-management-software-canada now route to the
              new ranked-listicle page (BestPropertyManagementCanada)
              higher up in this file — those routes intentionally win
              over the legacy PropertyManagementSoftwareCanada page. */}
          <Route path="/best-property-management-software" element={<PropertyManagementSoftwareCanada />} />
          {/* Alberta small-landlord niche SEO page (1–5 units). */}
          <Route path="/alberta-small-landlord-software" element={<AlbertaSmallLandlords />} />
          <Route path="/small-landlord-software-alberta" element={<AlbertaSmallLandlords />} />
          {/* Quebec French-language niche SEO page. */}
          <Route path="/logiciel-gestion-locative-quebec" element={<QuebecLandlordsFr />} />
          <Route path="/proprietaire-quebec-logiciel" element={<QuebecLandlordsFr />} />
          {/* Conversational discovery agent — replaces static contact
              forms with an interactive BANT questionnaire. Public route. */}
          <Route path="/discovery" element={<DiscoveryAgent />} />
          <Route path="/qualify"  element={<DiscoveryAgent />} />
          <Route path="/property-management-software/:city" element={<PropertyManagementCity flavor="software" />} />
          <Route path="/property-management-services/:city" element={<PropertyManagementCity flavor="services" />} />
          <Route path="/central-rentals-edmonton" element={<CentralRentalsEdmonton />} />
          <Route path="/community" element={<Protected><Community /></Protected>} />
          <Route path="/tenant-community" element={<Protected><Community scope="tenant"/></Protected>} />
          <Route path="/resources" element={<Protected><LandlordResources /></Protected>} />
          <Route path="/discounts" element={<Protected requireRole><LandlordDiscounts /></Protected>} />
          <Route path="/automations" element={<Protected requireRole><SmartAutomations /></Protected>} />
          <Route path="/automations/pricing" element={<Protected requireRole><PricingRecommendation /></Protected>} />
          <Route path="/automations/expenses" element={<Protected requireRole><ExpenseForecast /></Protected>} />
          <Route path="/marketing" element={<Protected requireRole><MarketingHub /></Protected>} />
          <Route path="/leasing-assistant" element={<Protected requireRole requirePaid feature="Leasing Assistant" benefits={["AI-drafted replies to lead emails and calls", "Showing confirmations in one click", "Every conversation logged and status-tracked"]}><LeasingAssistant /></Protected>} />
          <Route path="/rubs" element={<Protected requireRole requirePaid feature="RUBS Utility Split" benefits={["Split any master utility bill across every unit", "Sq-ft, occupant-count, or custom weights", "Posts to each tenant's ledger automatically"]}><RubsCalculator /></Protected>} />
          <Route path="/eviction" element={<Protected requireRole requirePaid feature="Eviction Workflow" benefits={["Province-correct N4 / N5 / N8 / N12 / N13 / RTB-30 drafts", "Affidavit of Service ready for the notary", "LTB / RTB / TAL application drafts"]}><EvictionWorkflow /></Protected>} />
          <Route path="/key-wall" element={<Protected requireRole requirePaid feature="Key Wall" benefits={["Digital sign-in / sign-out log for every physical key", "Overdue-return alerts", "Compliance-grade audit trail"]}><KeyWall /></Protected>} />
          <Route path="/gov" element={<Protected requireRole requirePaid feature="Government & Public-Housing Suite" benefits={["AI Fair-Housing & Section-8 auditor with statute citations", "5-to-10-year CapEx lifecycle forecast with budget-cap alerts", "Audit-ready reports for HUD / CMHC / municipal grants"]}><GovSuite /></Protected>} />
          <Route path="/for-government" element={<ForGovernment />} />
          <Route path="/cloud-browsers" element={<CloudBrowsers />} />
          <Route path="/crm/commercial-electrical-contractors" element={<CrmElectricalContractors />} />
          <Route path="/applications/:id" element={<Protected requireRole><ApplicationSummary /></Protected>} />
          <Route path="/developer" element={<Protected requireRole><ApiKeys /></Protected>} />
          <Route path="/stripe/complete" element={<StripeReturn />} />
          <Route path="/stripe/success" element={<StripeReturn />} />
          <Route path="/checkout/complete" element={<StripeReturn />} />
          <Route path="/checkout/success" element={<StripeReturn />} />
          <Route path="/report/landlord-2026-canada" element={<LandlordReport />} />
          <Route path="/for/:role" element={<ForRolePage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/property-management/:province" element={<PropertyManagementProvince />} />
          <Route path="/laws/:province" element={<LandlordLaws />} />
          <Route path="/laws" element={<LandlordLaws />} />
          <Route path="/university" element={<LandlordUniversity />} />
          {/* Internal admin tool — locked behind ADMIN_CONFIG_TOKEN */}
          <Route path="/admin/viralpost" element={<ViralPost />} />
          <Route path="/admin/follower" element={<Follower />} />
          <Route path="/admin/listing-studio" element={<ListingStudio />} />
          <Route path="/admin/marketing" element={<AdminMarketing />} />
          <Route path="/admin/claim" element={<AdminClaim />} />
          <Route path="/admin/error-log" element={<AdminErrorLog />} />
          <Route path="/admin/self-heal" element={<AdminSelfHeal />} />
          <Route path="/admin/funnel" element={<AdminFunnel />} />
          <Route path="/directory" element={<Directory />} />
          <Route path="/admin/crawler-insights" element={<AdminCrawlerInsights />} />
          <Route path="/tenant/renewal" element={<TenantRenewalPortal />} />
          <Route path="/tenant/toolkit" element={<TenantToolkit />} />
          <Route path="/landlord/leasing-assist" element={<LeasingAssist />} />
          <Route path="/partnership" element={<Partnership />} />
          <Route path="/referral-partners" element={<Partnership />} />
          <Route path="/resolutions" element={<LandlordResolution />} />
          <Route path="/resolutions/:slug" element={<LandlordResolution />} />
          <Route path="/admin/copilot-tasks" element={<AdminCopilotTasks />} />
          <Route path="/admin" element={<Navigate to="/admin/analytics" replace />} />
          <Route path="/admin/leads" element={<AdminLeads />} />
          <Route path="/equal-housing" element={<EqualHousing />} />
          <Route path="/admin/studio" element={<AdminStudio />} />
          <Route path="/admin/buildings" element={<AdminBuildings />} />
          <Route path="/admin/credit" element={<AdminCredit />} />
          <Route path="/admin/support" element={<AdminSupport />} />
          <Route path="/admin/property-data" element={<AdminPropertyData />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/admin/sms" element={<AdminSmsSettings />} />
          <Route path="/admin/sms-settings" element={<AdminSmsSettings />} />
          <Route path="/admin/webmaster" element={<AdminWebmasterVerify />} />
          <Route path="/admin/webmaster-verify" element={<AdminWebmasterVerify />} />
          <Route path="/admin/backlink-kit" element={<AdminBacklinkKit />} />
          <Route path="/admin/backlinks" element={<AdminBacklinkKit />} />
          <Route path="/admin/rank-tracker" element={<AdminRankTracker />} />
          <Route path="/admin/content-engine" element={<AdminContentEngine />} />
          <Route path="/admin/ad-studio" element={<AdminAdStudio />} />
          <Route path="/admin/content-pack" element={<AdminContentPack />} />
          <Route path="/admin/growth-os" element={<AdminGrowthOS />} />
          <Route path="/admin/seo-agent" element={<AdminSeoAgent />} />
          <Route path="/admin/geo-audit" element={<AdminGeoAudit />} />
          <Route path="/funds" element={<Protected requireRole><FlinksGate><LandlordFunds /></FlinksGate></Protected>} />
          <Route path="/affordable-housing" element={<Protected requireRole><AffordableHousingHub /></Protected>} />
          <Route path="/admin/auto-improve" element={<AdminAutoImprove />} />
          <Route path="/press-kit" element={<PressKit />} />
          <Route path="/press" element={<PressHub />} />
          <Route path="/press/forbes-brief" element={<PressForbesBrief />} />
          <Route path="/press/wealthsimple-guide" element={<PressWealthsimpleGuide />} />
          <Route path="/press/investopedia-explainer" element={<PressInvestopediaExplainer />} />
          <Route path="/press/outreach-kit" element={<PressOutreachKit />} />
          <Route path="/fr/press" element={<FrLocale enPath="/press"><PressHubFr /></FrLocale>} />
          <Route path="/fr/press/forbes-brief" element={<FrLocale enPath="/press/forbes-brief"><PressForbesBriefFr /></FrLocale>} />
          <Route path="/fr/press/wealthsimple-guide" element={<FrLocale enPath="/press/wealthsimple-guide"><PressWealthsimpleGuideFr /></FrLocale>} />
          <Route path="/fr/press/investopedia-explainer" element={<FrLocale enPath="/press/investopedia-explainer"><PressInvestopediaExplainerFr /></FrLocale>} />
          <Route path="/fr/press/outreach-kit" element={<FrLocale enPath="/press/outreach-kit"><PressOutreachKitFr /></FrLocale>} />
          <Route path="/local-listings" element={<LocalListings />} />
          <Route path="/investor-relations" element={<InvestorRelations />} />
          <Route path="/ipo-roadmap" element={<IpoRoadmap />} />
          <Route path="/invest" element={<Invest />} />
          <Route path="/fr/cas-d-usage/:slug" element={<FrenchUseCase />} />
          <Route path="/ai-tools" element={<AiTools />} />
          <Route path="/shared/:id" element={<SharedResult />} />
          <Route path="/find-tenants" element={<FindTenants />} />
          <Route path="/find-tenants/:city" element={<FindTenants />} />
          {/* Growth-lead tools — hidden from the free toolkit; accessible
              only to active subscribers via private links from the portal. */}
          <Route path="/tools/prospect-discovery" element={<Protected requireRole requirePaid feature="Prospect Discovery" benefits={["Build CASL-compliant lead lists of Canadian landlords & PM companies", "Directory search + verified contact enrichment", "One-tap outreach email + CSV export for your CRM"]}><ProspectDiscovery /></Protected>} />
          <Route path="/tools/ai-search-optimizer" element={<Protected requireRole requirePaid feature="Search Ranking Booster" benefits={["Grade your company page 0–100 for Google AI Overviews", "Instant fix-list to move your listing above competitors", "Push sitemap updates to Google + Bing on save"]}><AiSearchOptimizer /></Protected>} />
          <Route path="/tools/auto" element={<Protected requireRole requirePaid feature="Portfolio Copilot" benefits={["Drop-in action chips on every page in your portal", "Auto-parse leases, notices, offers and screenshots", "RTA correctness checks before you send a document"]}><Auto /></Protected>} />
          <Route path="/partners/apply" element={<PartnerApply />} />
          <Route path="/partners/:code" element={<PartnerDashboard />} />
          <Route path="/brokerages" element={<BrokerageReferral />} />
          <Route path="/enterprise" element={<EnterpriseIntegrations />} />
          <Route path="/tools" element={<ToolsRoute />} />
          <Route path="/tools/:tool" element={<ToolsRoute />} />
          <Route path="/library" element={<Library />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/group-drafts" element={<GroupDrafts />} />
          <Route path="/share-and-earn" element={<ShareAndEarn />} />
          <Route path="/changelog" element={<Changelog />} />

          {/* Auth-only, no role required */}
          <Route path="/role-select" element={
            <ProtectedNoShell><RoleSelect /></ProtectedNoShell>
          } />

          {/* Authenticated app */}
          <Route path="/dashboard" element={<Protected requireRole><Dashboard /></Protected>} />
          {/* Design-pass V2 dashboards — opt-in preview routes. Once
              validated with users these will replace /dashboard directly. */}
          <Route path="/dashboard-v2" element={<Protected requireRole><LandlordDashboardV2 /></Protected>} />
          <Route path="/tenant-home-v2" element={<Protected requireRole><TenantHomeV2 /></Protected>} />
          <Route path="/properties" element={<Protected requireRole requirePaid feature="Properties" benefits={["Unlimited properties + units", "Photo + document storage", "Public listing pages"]}><Properties /></Protected>} />
          <Route path="/properties/:id" element={<Protected requireRole><PropertyDetail /></Protected>} />
          <Route path="/leases" element={<Protected requireRole requirePaid feature="Leases" benefits={["Province-compliant lease templates", "E-signature flow", "Rent-roll dashboard"]}><Leases /></Protected>} />
          <Route path="/leases/:id" element={<Protected requireRole><LeaseDetail /></Protected>} />
          <Route path="/tickets" element={<Protected requireRole><Tickets /></Protected>} />
          <Route path="/tickets/:id" element={<Protected requireRole><TicketDetail /></Protected>} />
          <Route path="/payments" element={<Protected requireRole><Payments /></Protected>} />
          <Route path="/applications" element={<Protected requireRole><Applications /></Protected>} />
          <Route path="/expenses" element={<Protected requireRole><PaywallGuard feature="Expense tracking" benefits={["Categorized landlord expenses", "CSV export for tax season", "Receipt attachments"]}><Expenses /></PaywallGuard></Protected>} />
          <Route path="/billing" element={<Protected requireRole><Billing /></Protected>} />
          <Route path="/settings" element={<Protected requireRole><Settings /></Protected>} />
          <Route path="/settings/payment-instructions" element={<Protected requireRole><PaymentInstructions /></Protected>} />
          <Route path="/reports" element={<Protected requireRole><PaywallGuard feature="Reports & tax exports" benefits={["T776 / NR4 / T5013 generators", "Per-property income summaries", "Lifetime owner reports"]}><Reports /></PaywallGuard></Protected>} />
          <Route path="/vendors" element={<Protected requireRole><PaywallGuard feature="Vendor management" benefits={["Centralised vendor list", "Per-trade routing for tickets", "Insurance + W-9 storage"]}><Vendors /></PaywallGuard></Protected>} />
          <Route path="/work-orders" element={<Protected requireRole><PaywallGuard feature="Work orders" benefits={["Track maintenance jobs end-to-end", "Photo + cost attachments", "Vendor-billed reconciliation"]}><WorkOrders /></PaywallGuard></Protected>} />
          <Route path="/inspections" element={<Protected requireRole><PaywallGuard feature="Inspections" benefits={["Move-in / move-out checklists", "PDF reports with photos", "Reminder calendar"]}><Inspections /></PaywallGuard></Protected>} />
          <Route path="/insurance" element={<Protected requireRole><PaywallGuard feature="Insurance vault" benefits={["Store landlord + tenant policies", "Expiry alerts", "Claim history"]}><Insurance /></PaywallGuard></Protected>} />
          <Route path="/notices" element={<Protected requireRole><PaywallGuard feature="Provincial notices" benefits={["N1–N13 prefilled templates", "Send + audit trail", "Provincial law guidance"]}><Notices /></PaywallGuard></Protected>} />
          <Route path="/access-keys" element={<Protected requireRole><PaywallGuard feature="Smart access keys" benefits={["Schedule time-bound entry codes", "Per-unit audit log", "Self-tour automation"]}><AccessKeys /></PaywallGuard></Protected>} />
          <Route path="/smart-locks" element={<Protected requireRole requirePaid feature="Smart locks" benefits={["Yale + August + ButterflyMX", "Per-visit access codes", "Central audit log"]}><SmartLocks /></Protected>} />
          <Route path="/tenant/keys" element={<Protected><TenantKeys /></Protected>} />
          <Route path="/unlock" element={<KeylessUnlock />} />
          <Route path="/landlord/money" element={<Protected requireRole><LandlordMoneyHub /></Protected>} />
          <Route path="/landlord/concierge" element={<Protected requireRole requirePaid feature="Concierge Marketplace" benefits={["Vetted local leasing agents", "24/7 repair triage", "SLA-tracked response"]}><ConciergeHub /></Protected>} />
          <Route path="/service-workspace" element={<Protected><ServiceWorkspace /></Protected>} />
          <Route path="/service-requests/:id" element={<Protected><ServiceRequestDetail /></Protected>} />
          <Route path="/tenant/repairs" element={<Protected><TenantRepairs /></Protected>} />
          <Route path="/smart-tours" element={<Protected requireRole requirePaid feature="3D property tours" benefits={["Matterport 3D tours", "Video walkthroughs", "Self-tour scheduling"]}><SmartTours /></Protected>} />
          <Route path="/mls" element={<Protected requireRole><MlsListings /></Protected>} />
          <Route path="/parking" element={<Protected requireRole><LandlordParking /></Protected>} />
          <Route path="/emails" element={<Protected requireRole><EmailDrafter /></Protected>} />
          <Route path="/copilot" element={<Protected requireRole><CopilotHub /></Protected>} />
          <Route path="/migration" element={<Protected requireRole><MigrationWizard /></Protected>} />
          <Route path="/migration/buildium/how-to" element={<Protected requireRole><BuildiumHowTo /></Protected>} />
          <Route path="/pm-workspace" element={<Protected requireRole><PMWorkspace /></Protected>} />
          <Route path="/settings/automation" element={<Protected requireRole><AutoAdvanceSettings /></Protected>} />
          <Route path="/use-cases" element={<UseCaseLanding />} />
          <Route path="/use-cases/:slug" element={<UseCaseLanding />} />
          <Route path="/subleases" element={<SubleaseMarketplace />} />
          <Route path="/subleases/:id" element={<SubleaseMarketplace />} />
          <Route path="/helpdesk" element={<Protected><TenantHelpDesk /></Protected>} />
          <Route path="/maintenance" element={<Protected requireRole><MaintenanceRequests /></Protected>} />
          <Route path="/rent-receipts" element={<Protected requireRole><TenantBillingExtras /></Protected>} />
          <Route path="/pay-bills" element={<Protected requireRole><PayBills /></Protected>} />
          <Route path="/my-vendors" element={<Protected requireRole><MyVendors /></Protected>} />
          <Route path="/leases/:leaseId/envelopes" element={<Protected requireRole><LeaseEnvelopes /></Protected>} />
          <Route path="/esign/:eid" element={<Protected><LeaseSign /></Protected>} />
          <Route path="/cra-filing" element={<Protected><CRAFiling /></Protected>} />
          <Route path="/team" element={<Protected requireRole><LandlordTeam /></Protected>} />
          <Route path="/team/accept/:token" element={<AcceptInvite />} />
          <Route path="/announcements" element={<Protected requireRole><Announcements /></Protected>} />
          <Route path="/tenant/announcements" element={<Protected requireRole><TenantAnnouncements /></Protected>} />
          <Route path="/cash-app" element={<Protected requireRole><CashApp /></Protected>} />
          <Route path="/admin/integrations" element={<IntegrationsHealth />} />
          <Route path="/admin/letters" element={<AdminLetters />} />
          <Route path="/settings/bank" element={<Protected requireRole><FlinksGate><BankAccountSettings /></FlinksGate></Protected>} />
          <Route path="/smart-home" element={<Protected requireRole><PaywallGuard feature="Smart home" benefits={["Schedule time-bound entry codes", "Per-unit audit log", "Self-tour automation"]}><AccessKeys /></PaywallGuard></Protected>} />
          <Route path="/tours" element={<Protected requireRole><PaywallGuard feature="Marketing & tours" benefits={["Self-tour booking links", "Video tour hosting", "Local-SEO marketing pack"]}><Tours /></PaywallGuard></Protected>} />
          <Route path="/messages" element={<Protected requireRole requirePaid feature="Messages" benefits={["Reply Copilot drafts", "Landlord ↔ tenant threads", "Unlimited history"]}><Messages /></Protected>} />
          <Route path="/documents" element={<Protected requireRole><PaywallGuard feature="Documents vault" benefits={["Secure cloud storage", "Share with tenants & investors", "Lease + addendum archive"]}><Documents /></PaywallGuard></Protected>} />
          <Route path="/wellness" element={<Protected requireRole><Wellness /></Protected>} />
          <Route path="/late-rent-autopilot" element={<Protected requireRole requirePaid feature="Late-rent autopilot" benefits={["Escalating reminders", "Auto-drafted N4 / L1", "LTB-admissible audit trail"]}><LateRentAutopilot /></Protected>} />
          <Route path="/rent-score" element={<Protected requireRole><TenantScore /></Protected>} />
          <Route path="/rent-calculator" element={<Protected requireRole><RentCalculator /></Protected>} />
          <Route path="/review" element={<Protected requireRole><LeaveReview /></Protected>} />
          <Route path="/utilities" element={<Protected requireRole><PaywallGuard feature="Utilities" benefits={["Sync hydro, water, gas, internet accounts in one place", "Auto-charge tenants their share of split bills", "Reminders before due dates"]}><Utilities /></PaywallGuard></Protected>} />
          <Route path="/compliance" element={<Protected requireRole><PaywallGuard feature="Compliance" benefits={["Provincial filing deadline tracking", "Inspection certificates & expiry alerts", "One-click audit reports"]}><Compliance /></PaywallGuard></Protected>} />
          <Route path="/rent-analysis" element={<Protected requireRole><PaywallGuard feature="Rent analysis" benefits={["Market-rate comparison for your area", "Suggested rent adjustments", "Vacancy risk score"]}><RentAnalysis /></PaywallGuard></Protected>} />
          <Route path="/assistant" element={<Protected requireRole><PaywallGuard feature="Ask Mark Anything" benefits={["Mark drafts notices, leases, and tenant emails", "Maintenance triage suggestions", "Compliance Q&A on demand"]}><Assistant /></PaywallGuard></Protected>} />
          <Route path="/investor" element={<Protected requireRole><PaywallGuard feature="Investor portal" allowInvestor benefits={["T5013 / NR4 tax exports", "Distribution waterfalls", "Capital call tracking"]}><Investor /></PaywallGuard></Protected>} />
          <Route path="/referrals" element={<Protected requireRole><Referrals /></Protected>} />
          <Route path="/smart" element={<Protected requireRole><PaywallGuard feature="Smart features" benefits={["8 agentic smart features powered by Claude Sonnet", "Maintenance triage, visual condition assessment, retention risk, anomaly detection", "Conversational ledger, multi-agent orchestration, CRA-aware bookkeeping, portfolio benchmarking"]}><SmartFeatures /></PaywallGuard></Protected>} />
          <Route path="/books" element={<Protected requireRole><PaywallGuard feature="Books" benefits={["P&L per property and portfolio rollups", "CRA-ready T776 export", "Bank reconciliation with Flinks", "General ledger CSV for QuickBooks / Xero"]}><Books /></PaywallGuard></Protected>} />
          <Route path="/bills" element={<Protected requireRole requirePaid feature="Bills · AP" benefits={["Track landlord bills", "Vendor + property tagging", "One-click pay"]}><Bills /></Protected>} />
          <Route path="/bank-recon" element={<Protected requireRole requirePaid feature="Bank reconciliation" benefits={["Flinks bank sync", "Auto-matched transactions", "Uncategorized queue"]}><BankRecon /></Protected>} />
          <Route path="/briefing" element={<Protected requireRole requirePaid feature="Daily briefing" benefits={["Audio + written briefings", "Portfolio snapshot", "Prioritised action list"]}><Briefing /></Protected>} />
          <Route path="/activity" element={<Protected requireRole><Activity /></Protected>} />
          <Route path="/agent-settings" element={<Protected requireRole requirePaid feature="Agent settings" benefits={["Custom copilot tone", "Notification defaults", "Team roles"]}><AgentSettings /></Protected>} />          <Route path="/smart-operator" element={<Protected requireRole requirePaid feature="Smart Operator" benefits={["24/7 tenant intake copilot", "Auto-routed maintenance", "Contextual escalation"]}><SmartOperator /></Protected>} />
          <Route path="/integrations" element={<Protected requireRole requirePaid feature="Integrations" benefits={["Stripe · CondoWorks · Butterfly MX", "Matterport · MLS · Certn", "Zapier + QuickBooks (coming)"]}><IntegrationsHub /></Protected>} />
          <Route path="/integrations/condoworks" element={<Protected requireRole><CondoWorksIntegration /></Protected>} />
          <Route path="/integrations/butterflymx" element={<Protected requireRole><ButterflyMxIntegration /></Protected>} />
          <Route path="/retention" element={<Protected requireRole requirePaid feature="Long-term tenants" benefits={["Retention scoreboard per tenant", "Renewal reminders", "Churn-risk heatmap"]}><Retention /></Protected>} />
          <Route path="/my-business" element={<Protected requireRole><MyBusiness /></Protected>} />
          <Route path="/internal" element={<InternalWorkspace />} />
          <Route path="/vendor-apply" element={<VendorApply />} />
          {/* Alias — historic links + Stripe return URLs point to /vendors/apply. */}
          <Route path="/vendors/apply" element={<VendorApply />} />
          <Route path="/vendors/:id" element={<VendorDetail />} />
          <Route path="/revenue-engine" element={<Protected requireRole requirePaid feature="Revenue Engine" benefits={["Rent-optimization suggestions", "Occupancy forecasting", "Concession tracking"]}><RevenueEngine /></Protected>} />
          <Route path="/tenant-letters" element={<Protected requireRole requirePaid feature="Tenant letters" benefits={["Provincial-compliant letter templates", "Bulk send", "Delivery audit trail"]}><TenantLetters /></Protected>} />
          <Route path="/screening" element={<Protected requireRole><TenantScreening /></Protected>} />
          <Route path="/screening/consent/:token" element={<ScreeningConsent />} />

          {/* Building Hub — subscriber-only single command-centre per property */}
          <Route path="/buildings" element={<Protected requireRole requirePaid feature="Building Hub" benefits={["Every property in one command-centre", "Live occupancy + monthly-rent rollups", "Quick actions for maintenance, screening, flyers"]}><Buildings /></Protected>} />
          <Route path="/buildings/:propertyId" element={<Protected requireRole requirePaid feature="Building Hub" benefits={["Every property in one command-centre", "Live occupancy + monthly-rent rollups", "Quick actions for maintenance, screening, flyers"]}><BuildingHub /></Protected>} />

          {/* Marketing Assets — subscriber-only listing flyer / social studio */}
          <Route path="/marketing-assets" element={<Protected requireRole requirePaid feature="Marketing Assets" benefits={["Printable A4 flyers with modern wording", "Instagram / Facebook social squares", "Ready-to-paste Kijiji + Marketplace text"]}><MarketingAssets /></Protected>} />

          {/* Tenant Placement — subscriber-only public directory publisher */}
          <Route path="/tenant-placement" element={<Protected requireRole requirePaid feature="Tenant Placement" benefits={["Publish to a Canadian rental directory", "Direct tenant enquiries, no listing fees", "Boost your listing for 7 days"]}><TenantPlacement /></Protected>} />

          {/* Public rental directory + listing detail — SEO */}
          <Route path="/find-a-place" element={<FindAPlace />} />
          <Route path="/find-a-place/:city" element={<FindAPlace />} />
          <Route path="/rental/:slug" element={<RentalListing />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
        <ServiceWorkQuietMode><SmartOperatorBubble /><CopilotSearchWidget /><PWAInstallPrompt /></ServiceWorkQuietMode>
      </BrowserRouter>
    </AuthProvider>
  );
}

function ProtectedNoShell({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to={`/login/${lastPortal()}`} replace />;
  return children;
}

export default App;
